import * as Sentry from '@sentry/gatsby';

import type { ImageHandlerRequest } from '@models/image-handler';
import { ImageFormatTypes } from '@models/image-handler';

/**
 * Get the nearest supported dimension of an image
 * @param size Requested size of an image
 * @returns Nearest supported size of the image
 */
function roundToNearestSize(size: number) {
  const supportedSizes = [48, 64, 128, 256, 512];
  return supportedSizes.reduce((prev, curr) => {
    return Math.abs(curr - size) < Math.abs(prev - size) ? curr : prev;
  });
}

/**
 * Get an edited image from an image on S3
 * @param s3_key Publicly accessible S3 object key of an image file
 * @param width Desired width of the image
 * @param height Desired height of the image
 * @returns Edited image
 */
export function getImageSrc(
  s3_key: string | null | undefined,
  width: number,
  height: number
) {
  if (!s3_key || process.env.NODE_ENV === 'test') {
    return null;
  }

  if (!process.env.GATSBY_S3_BUCKET_AVATAR_UPLOADS) {
    Sentry.captureException(
      `GATSBY_S3_BUCKET_AVATAR_UPLOADS is not provided: ${process.env.GATSBY_S3_BUCKET_AVATAR_UPLOADS}`
    );
  }

  if (width === height) {
    width = roundToNearestSize(width);
    height = roundToNearestSize(height);
  } else {
    width = roundToNearestSize(width);
    height = (roundToNearestSize(width) * height) / width;
  }

  const imageHandlerUrl =
    'https://wn7g0eht42.execute-api.ca-central-1.amazonaws.com/image';
  const imageRequest: ImageHandlerRequest = {
    bucket: process.env.GATSBY_S3_BUCKET_AVATAR_UPLOADS ?? '',
    key: s3_key,
    edits: {
      toFormat: ImageFormatTypes.JPG,
      resize: {
        width,
        height,
      },
    },
  };
  const b64Request = Buffer.from(JSON.stringify(imageRequest)).toString(
    'base64'
  );
  const src = s3_key ? `${imageHandlerUrl}/${b64Request}` : null;
  return src;
}
